import { useState } from "react";
import { MapPin, Navigation, Search, X } from "lucide-react";
import api, { apiError } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { toast } from "sonner";

export function LocationEditor({ onDone, onClose }) {
  const { refresh } = useAuth();
  const [busy, setBusy] = useState(false);
  const [q, setQ] = useState("");
  const [results, setResults] = useState([]);

  const useGPS = () => {
    if (!navigator.geolocation) { toast.error("Geolocation not supported. Type a city."); return; }
    setBusy(true);
    navigator.geolocation.getCurrentPosition(async (pos) => {
      try {
        const { latitude, longitude } = pos.coords;
        const { data } = await api.get(`/location/reverse?lat=${latitude}&lng=${longitude}`);
        await api.put("/location", { ...data });
        await refresh();
        toast.success(`Location set: ${data.city || "found"}`);
        onDone?.();
      } catch (e) { toast.error(apiError(e.response?.data?.detail)); }
      finally { setBusy(false); }
    }, () => { setBusy(false); toast.error("Permission denied. Type a city instead."); });
  };

  const search = async () => {
    if (!q.trim()) return;
    setBusy(true);
    try {
      const { data } = await api.get(`/location/search?q=${encodeURIComponent(q)}`);
      setResults(data);
    } catch (e) { toast.error("Search failed"); }
    finally { setBusy(false); }
  };

  const pick = async (r) => {
    setBusy(true);
    try {
      await api.put("/location", {
        lat: r.lat, lng: r.lng, city: r.city, county: r.county,
        region: r.region, country: r.country, locationSource: "typed",
      });
      await refresh();
      toast.success(`Location set: ${r.city || r.label}`);
      onDone?.();
    } catch (e) { toast.error(apiError(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  return (
    <div className="pm-card p-5" data-testid="location-editor">
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center gap-2"><MapPin className="w-4 h-4" /><span className="font-head font-semibold">Set your place</span></div>
        {onClose && <button onClick={onClose} data-testid="location-close"><X className="w-4 h-4" /></button>}
      </div>
      <p className="text-xs mb-3" style={{ color: "var(--text-2)" }}>Used to tailor local prices, licenses, and ideas. Not sold.</p>
      <button onClick={useGPS} disabled={busy} className="pm-btn flex items-center gap-2 w-full justify-center mb-3" data-testid="use-location-btn">
        <Navigation className="w-4 h-4" /> Use my location
      </button>
      <div className="label-mono text-center mb-3">or type a city</div>
      <div className="flex gap-2">
        <input className="pm-input" placeholder="City, region, country" value={q}
          onChange={(e) => setQ(e.target.value)} onKeyDown={(e) => e.key === "Enter" && search()} data-testid="city-input" />
        <button onClick={search} disabled={busy} className="pm-btn px-3" data-testid="city-search-btn"><Search className="w-4 h-4" /></button>
      </div>
      {results.length > 0 && (
        <div className="mt-3 space-y-1" data-testid="location-results">
          {results.map((r, i) => (
            <button key={i} onClick={() => pick(r)} className="pm-btn-ghost w-full text-left text-sm py-2" data-testid={`location-result-${i}`}>
              {r.label}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}
